import React, { useState, ReactNode, useEffect, useMemo, useRef } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSharedTranslations } from "../../lib/i18n";
import { OptionType, SearchableSelect } from "../react-select/searchable-select/searchable-select";
import { Button } from "../button/button";
import { Icon } from "../icons/icon";
import { SortableList } from "../sortable-list/sortable-list";
import styles from "./select-list.module.scss";

interface ISelectListProps {
    value?: string[];
    options: IdOptionType[]
    className?: string;
    onChange: (val: string[]) => void
    actionButton?: ReactNode;
    onActionButtonClick?: (index: number) => void;
    resetFactor?: any;
    draggable?: boolean;
    renderButtonGroup?: (node: ReactNode) => void;
    addButtonLabel?: string;

}

export interface IdOptionType extends OptionType {
    id: string | number;
}

export const SelectList: React.FC<ISelectListProps> = React.memo(({
                                                                      value,
                                                                      options,
                                                                      className,
                                                                      onChange,
                                                                      actionButton,
                                                                      onActionButtonClick,
                                                                      resetFactor,
                                                                      renderButtonGroup,
                                                                      draggable = true,
                                                                      addButtonLabel,
                                                                  }) => {
    const [selectValue, setSelectValue] = useState<string[]>([]);
    const [optionSelectActivated, setOptionSelectActivated] = useState(false);
    const elementsRef = useRef<HTMLDivElement>(null);
    const t = useSharedTranslations();
    const { i18n } = useTranslation();

    useEffect(() => {
        renderButtonGroup && renderButtonGroup(<div className={styles.buttonGroup}>
            <Button size="m"
                    onClick={onAddAllClick}
                    view="outline">
                <div className={styles.optionText}>
                    <Icon glyph="plusCircle"/>
                    <span>{t("addAll")}</span>
                </div>
            </Button>
            <Button size="m"
                    onClick={onDeleteAllClick}
                    view="outline">
                <div className={styles.optionText}>
                    <Icon glyph="trash" className={styles.trashIcon}/>
                    <span>{t("deleteAll")}</span>
                </div>
            </Button>
        </div>)
    }, [options, selectValue, i18n.language])

    useEffect(() => {
        setSelectValue(value && value.length > 0 ? value : []);
    }, [value])

    useEffect(() => {
        setOptionSelectActivated(false);
    }, [resetFactor])

    const sortableComponents = useMemo(() => {
        return selectValue.map((v) => options.find((o) => o.value === v) as IdOptionType).filter(v => !!v);
    }, [selectValue, options]);

    const scrollSelectToEnd = () => {
        setTimeout(() => {
            if (elementsRef.current) {
                elementsRef.current.scrollTop = elementsRef.current.scrollHeight;
            }
        }, 100)
    }

    const setValue = (val: string, index: number) => {
        setSelectValue((prevState) => {
            const updatedState = [...prevState];
            updatedState[index] = val;
            onChange(updatedState);
            return updatedState;
        })
    }

    const onAddAllClick = () => {
        const allRemainingValues = filterOptions().map(o => o.value);
        setSelectValue((prevState) => {
            const updatedState = [...prevState, ...allRemainingValues];
            onChange(updatedState);
            return updatedState;
        })
    }

    const onDeleteAllClick = () => {
        onChange([]);
        setSelectValue([]);
    }

    const onDeleteOption = (index: number) => {
        setSelectValue((prevState) => {
            const updatedState = [...prevState];
            updatedState.splice(index, 1);
            onChange(updatedState);
            return updatedState;
        })
    }

    const setNewValue = (val: string) => {
        setSelectValue((prevState) => {
            const updatedState = [...prevState];
            updatedState.push(val);
            onChange(updatedState);
            return updatedState;
        })
        setOptionSelectActivated(false);
        scrollSelectToEnd();
    }

    const onComponentsSort = (options: IdOptionType[]) => {
        const updatedValue = options.map((o) => o.value);
        onChange(updatedValue);
        setSelectValue(updatedValue);
    }

    const onAddOptionClick = () => {
        setOptionSelectActivated(true);
    }

    const filterOptions = (item?: IdOptionType) => {
        const filteredIds = sortableComponents.map((c) => c.id).filter(id => item ? item.id !== id : true);
        return options.filter((o) => !filteredIds.includes(o.id));
    }

    return <div className={cn(styles.container, className)}>
        <div className={styles.sortableContainer} ref={elementsRef}>
            <SortableList<IdOptionType>
                items={sortableComponents}
                onChange={onComponentsSort}
                renderItem={(item: IdOptionType, index: number) => (
                    <SortableList.Item id={item.id}>
                        {draggable && <SortableList.DragHandle/>}
                        <div className={styles.selectContainer}>
                            <SearchableSelect
                                options={filterOptions(item)}
                                className={styles.selectField}
                                optionValue={item}
                                onChange={(val) => {
                                    setValue(val as string, index)
                                }}
                                isMulti={false}
                                hideClearIcon={true}
                                isSearchable={true}
                            />
                            {actionButton && <div onClick={() => onActionButtonClick && onActionButtonClick(index)}
                                                  className={styles.actionButton}>{actionButton}</div>}
                            <div onClick={() => onDeleteOption(index)}><Icon glyph="trash"
                                                                             className={styles.deleteIcon}/>
                            </div>
                        </div>
                    </SortableList.Item>
                )}
            />
        </div>

        {optionSelectActivated && <div className={styles.newFieldContainer}>
            {draggable && <SortableList.DragHandle className={styles.disabledDragHandle}/>}
            <SearchableSelect
                options={filterOptions()}
                className={styles.selectField}
                onChange={(val) => {
                    setNewValue(val as string)
                }}
                isMulti={false}
                hideClearIcon={true}
                isSearchable={true}
            />
            {actionButton && <div>{actionButton}</div>}
            <div onClick={() => setOptionSelectActivated(false)}><Icon glyph="trash" className={styles.deleteIcon}/>
            </div>
        </div>}
        <Button size="m"
                disabled={optionSelectActivated || filterOptions().length === 0}
                className={styles.addOptionButton}
                onClick={onAddOptionClick}
                view="outline">
            <div className={styles.optionText}>
                <Icon glyph="plusCircle"/>
                <span>{addButtonLabel ?? t("addValue")}</span>
            </div>
        </Button>
    </div>
});
